import { DeleteOutlined } from '@ant-design/icons';
import { Button, Checkbox, Descriptions, Result, Space, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { AxiosError } from 'axios';

import { AccessDependencyDetail } from '@app/domain-access-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';

import { EmployeeTypeRenderer } from '../../../../common';
import { ApproverTable } from '../../../../subscriptions/modals/approver-table';
import { useDeleteDependency, useFindAccessById, useGetAccessDependency } from '../../data';

export type AccessViewPanelProps = {
  id: number;
  showAccessList?: boolean;
};

export const AccessViewPanel = ({ id, showAccessList = true }: AccessViewPanelProps) => {
  const findAccessQuery = useFindAccessById(id);
  const findAccessDependencyQuery = useGetAccessDependency(id);
  const deleteDependencyQuery = useDeleteDependency(id);

  const accessColumns: ColumnsType<AccessDependencyDetail> = [
    {
      title: 'Action',
      render: (_, record) => (
        <Button icon={<DeleteOutlined />} size="small" onClick={() => deleteDependencyQuery.mutate(record.parentId)}>
          Delete
        </Button>
      ),
    },
    {
      title: 'Name',
      dataIndex: ['parent', 'name'],
      key: 'name',
    },
    {
      title: 'Source',
      dataIndex: ['parent', 'accessSource'],
      width: 120,
    },
    {
      title: 'Emp Type',
      dataIndex: ['parent', 'employeeType'],
      width: 120,
      render: (value) => <EmployeeTypeRenderer employeeType={value} />,
    },
    {
      title: 'Created By',
      dataIndex: ['createdByUser', 'name'],
    },
    {
      title: 'Created On',
      dataIndex: ['createdOn'],
    },
  ];

  if (findAccessQuery.error) {
    const error = findAccessQuery.error as AxiosError;

    if (error.response?.status === 404) {
      return <Result status="404" title="This bundle does not exist or was deleted." />;
    }

    return <Result status="error" title="Something went wrong." />;
  }

  if (findAccessQuery.isPending && (!showAccessList || findAccessDependencyQuery.isPending)) {
    return <div>Loading...</div>;
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Descriptions bordered size="small" layout="horizontal" column={2}>
        <Descriptions.Item label="Name">{findAccessQuery.data?.name}</Descriptions.Item>
        <Descriptions.Item label="Active">
          <Checkbox checked={findAccessQuery.data?.isActive}></Checkbox>
        </Descriptions.Item>
        <Descriptions.Item label="Employee Type">
          <EmployeeTypeRenderer employeeType={findAccessQuery.data?.employeeType} />
        </Descriptions.Item>

        <Descriptions.Item label="Updated By">{findAccessQuery.data?.updatedByUser.name}</Descriptions.Item>
        <Descriptions.Item label="Updated On">
          {findAccessQuery.data?.updatedOn ? formatIsoDate(findAccessQuery.data?.updatedOn.toString()) : undefined}
        </Descriptions.Item>
        <Descriptions.Item label="Created By">{findAccessQuery.data?.createdByUser.name}</Descriptions.Item>
        <Descriptions.Item label="Created On" span={2}>
          {findAccessQuery.data?.createdOn ? formatIsoDate(findAccessQuery.data?.createdOn.toString()) : undefined}
        </Descriptions.Item>
        {showAccessList && findAccessQuery.data?.accessSource.length ? (
          <Descriptions.Item label="Approvers" span={2}>
            <ApproverTable
              accessSource={findAccessQuery?.data?.accessSource}
              externalId={findAccessQuery?.data?.externalId}
              name={findAccessQuery?.data?.name}
            ></ApproverTable>
          </Descriptions.Item>
        ) : undefined}
      </Descriptions>

      {showAccessList && findAccessDependencyQuery.data?.data.length ? (
        <>
          <Typography.Title level={5}>Dependencies</Typography.Title>
          <Table
            rowKey={(e) => e.id}
            columns={accessColumns}
            dataSource={findAccessDependencyQuery.data?.data}
            size="small"
            pagination={false}
          />
        </>
      ) : undefined}
    </Space>
  );
};
